import React, { useEffect } from 'react';
import { ScrollView, View } from 'react-native';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import { map as _map, get as _get, isFunction as _isFunction, isEmpty as _isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import { VFText, ErrorScreen } from '@vfgroup-oneplatform/foundation/Components';
import {
  withTheme,
  LightThemeColors,
  Types,
} from '@vfgroup-oneplatform/foundation/Components/Themes';
import { usePrevious } from '@vfgroup-oneplatform/foundation/Utils/Hooks';

import FrameworkTestKeys from '../../../FrameworkTestKeys';
import { getThemeImages } from '../../../Themes';
import { MyPlanCard, MyPlanCardLoading, MyGroupDataPlan } from '../../Components';
import useMyPlanExtrasModal from '../../Hooks/useMyPlanExtrasModal';
import MyPlanExtrasModal from '../MyPlanExtrasModal';
import Config from '../MyPlanExtrasModal/Config.json';

import styles from './MyPlan.Styles';

const MyPlan = ({
  withTray,
  title,
  subtitle,
  planData,
  groupData,
  containerStyle,
  images,
  theme,
  addExtraPlanData,
  onPressAddConfirmation,
  forceOpenModal,
  paymentCards,
  defaultPayment,
  onSelectDefaultCard,
  onPressSaveCard,
  onAddUsagePress,
  onClose,
  isMyPlanLoading,
  isError,
  onTryAgainPress,
  errorText,
  errorImages,
  errorIconName,
  tryAgainIconName,
  renderMyPlanFooter,
  upcomingPlan,
  ...props
}) => {
  const {
    toggleModal,
    isVisible,
    closeModal,
    isLoading,
    saveCardIsLoading,
    extraModalJson,
    handlePaymentFooterIconPress,
    handleConfirmButtonPress,
    isLoadingSuccessful,
    doneLoadingSubtitlePlaceholders,
    isResetData,
    handleSelectDefaultCard,
    onPressOpenCardList,
    paymentOptions,
    setPaymentOptions,
    onPressAddCard,
    handlePressSaveNewCard,
    onBackButtonPress,
    loadingFailureError,
    toggleAddUsageModal,
  } = useMyPlanExtrasModal({
    Config,
    addExtraPlanData,
    onPressAddConfirmation,
    onSelectDefaultCard,
    onPressSaveCard,
  });
  const prevVisable = usePrevious(isVisible);

  useEffect(() => {
    forceOpenModal && forceOpenModal.force && toggleAddUsageModal(forceOpenModal.key);
  }, [forceOpenModal && forceOpenModal.force]);

  useEffect(() => {
    !isVisible && setPaymentOptions({});
    if (prevVisable && !isVisible) {
      onClose && onClose();
    }
  }, [isVisible, prevVisable]);
  useEffect(() => {
    if (
      forceOpenModal &&
      forceOpenModal.force &&
      _isFunction(forceOpenModal.onModalVisibilityChange)
    ) {
      forceOpenModal.onModalVisibilityChange(isVisible);
    }
  }, [isVisible]);

  const Images = getThemeImages(theme.name);

  const renderErrorScreen = () => {
    return (
      <ErrorScreen
        iconStyle={styles.errorIconStyle}
        text={errorText}
        images={errorImages}
        iconName={errorIconName}
        iconButtonName={tryAgainIconName}
        onPress={onTryAgainPress}
      />
    );
  };

  const renderSubtitle = sectionSubtitle => {
    return sectionSubtitle && !isError ? (
      <VFText
        testKey={FrameworkTestKeys.myPlanCardSubtitle}
        i18nKey={sectionSubtitle}
        style={styles.headerSubTitle(theme)}
      />
    ) : null;
  };

  const renderMyPlanCards = () => {
    const isMultiplePlans = Array.isArray(planData) && !_isEmpty(planData);
    const planCards = isMultiplePlans
      ? planData
      : [
          {
            ...planData,
            ...props,
          },
        ];
    const cards = _map(planCards, _card => (
      <View testID={FrameworkTestKeys.myPlanCardView}>
        {renderSubtitle(_card.subtitle)}
        <MyPlanCard
          {..._card}
          onAddUsagePress={onAddUsagePress}
          toggleAddUsageModal={toggleAddUsageModal}
          images={images}
        />
      </View>
    ));
    return (
      <>
        {renderSubtitle(upcomingPlan?.subtitle)}
        {cards}
        {groupData && <MyGroupDataPlan groupData={groupData} images={images} />}
      </>
    );
  };

  const renderMyPlanCardsOptions = () => {
    return isError ? renderErrorScreen() : renderMyPlanCards();
  };

  return (
    <ScrollView
      scrollEnabled={!isMyPlanLoading}
      style={[styles.container, containerStyle]}
      contentContainerStyle={styles.scrollViewContainerStyle(withTray)}
    >
      <MyPlanExtrasModal
        toggleModal={toggleModal}
        isVisible={isVisible}
        closeModal={closeModal}
        type={Types.SECONDARY}
        isLoading={isLoading}
        saveCardIsLoading={saveCardIsLoading}
        title={_get(extraModalJson, 'title', '')}
        paymentTypeTitle={_get(extraModalJson, 'paymentTypeTitle', '')}
        paymentTypeTitleStyle={{}}
        paymentTypeOptions={_get(extraModalJson, 'options', [])}
        paymentAmountTitle={_get(extraModalJson, 'paymentAmountTitle', '')}
        paymentAmountFirstTypeOptions={_get(extraModalJson, 'options[0].items', [])}
        paymentAmountSecondTypeOptions={_get(extraModalJson, 'options[1].items', [])}
        paymentMethodTitle={_get(extraModalJson, 'paymentMethod.title', '')}
        paymentMethodTitleStyle={{}}
        paymentFooterTitle={_get(extraModalJson, 'paymentMethod.subTitle', '')}
        paymentFooterIcon={Images.edit_pen}
        handlePaymentFooterIconPress={handlePaymentFooterIconPress}
        paymentFooterFirstTypeDescription={_get(extraModalJson, 'options[0].description', '')}
        paymentFooterSecondTypeDescription={_get(extraModalJson, 'options[1].description', '')}
        confirmButtonType={Types.PRIMARY}
        confirmButtonTitle={_get(extraModalJson, 'paymentMethod.firstButtonText', '')}
        handleConfirmButtonPress={handleConfirmButtonPress}
        cancelButtonType={Types.SECONDARY}
        cancelButtonTitle={_get(extraModalJson, 'paymentMethod.secondButtonText', '')}
        handleCancelButtonPress={closeModal}
        loadingMessage={_get(extraModalJson, 'loadingScreen.loadingMessage', '')}
        isLoadingSuccessful={isLoadingSuccessful}
        doneLoadingFailureIcon={Images.ic_WarningHiLight_Theme}
        doneLoadingMainTitle={
          isLoadingSuccessful
            ? _get(extraModalJson, 'resultScreen.title', '')
            : _get(loadingFailureError, 'message', '')
        }
        doneLoadingSubTitle={
          isLoadingSuccessful
            ? _get(extraModalJson, 'resultScreen.message', '')
            : _get(loadingFailureError, 'description', '')
        }
        doneLoadingSubtitlePlaceholders={doneLoadingSubtitlePlaceholders}
        doneLoadingPrimaryButtonType={Types.PRIMARY}
        doneLoadingPrimaryButtonText={
          isLoadingSuccessful
            ? _get(extraModalJson, 'resultScreen.buttonText', '')
            : _get(extraModalJson, 'resultScreen.failureButtonText', '')
        }
        doneLoadingPrimaryButtonAction={isLoadingSuccessful ? closeModal : null}
        isResetData={isResetData}
        paymentCards={paymentCards}
        defaultPayment={defaultPayment}
        onSelectDefaultCard={handleSelectDefaultCard}
        onPressOpenCardList={onPressOpenCardList}
        paymentOptions={paymentOptions}
        onPressAddCard={onPressAddCard}
        onPressSaveCard={handlePressSaveNewCard}
        onBackButtonPress={onBackButtonPress}
      />
      <VFText
        testKey={FrameworkTestKeys.myPlanTitle}
        i18nKey={title}
        style={styles.headerTitle(theme)}
      />
      {renderSubtitle(subtitle)}
      {isMyPlanLoading ? <MyPlanCardLoading /> : renderMyPlanCardsOptions()}
      {_isFunction(renderMyPlanFooter) && renderMyPlanFooter()}
    </ScrollView>
  );
};

MyPlan.defaultProps = {
  theme: LightThemeColors,
  renderMyPlanFooter: () => null,
};

MyPlan.propTypes = {
  statusStyle: ViewPropTypes.style,
  onClose: PropTypes.func,
  defaultPayment: PropTypes.object,
  onSelectDefaultCard: PropTypes.func,
  onPressSaveCard: PropTypes.func,
  withTray: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  containerStyle: ViewPropTypes.style,
  images: PropTypes.object,
  planData: PropTypes.oneOf([PropTypes.object, PropTypes.array]),
  groupData: PropTypes.shape({
    status: PropTypes.string,
    title: PropTypes.string,
    renewalDate: PropTypes.string,
    usageData: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        icon: PropTypes.string,
        leftText: PropTypes.string,
        rightText: PropTypes.string,
        percentageValue: PropTypes.number,
      }),
    ),
  }),
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  addExtraPlanData: PropTypes.object,
  onPressAddConfirmation: PropTypes.func,
  forceOpenModal: PropTypes.shape({
    force: PropTypes.bool,
    key: PropTypes.string,
    onModalVisibilityChange: PropTypes.func,
  }),

  paymentCards: PropTypes.arrayOf(
    PropTypes.shape({
      brand: PropTypes.string,
      lastFourDigits: PropTypes.string,
      cardName: PropTypes.string,
      expiry: PropTypes.string,
      icon: PropTypes.object,
    }),
  ),
  onAddUsagePress: PropTypes.func,
  isMyPlanLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onTryAgainPress: PropTypes.func,
  errorText: PropTypes.string,
  errorImages: PropTypes.array,
  errorIconName: PropTypes.string,
  tryAgainIconName: PropTypes.string,
  renderMyPlanFooter: PropTypes.func,
  upcomingPlan: PropTypes.shape({
    subtitle: PropTypes.string,
  }),
};
export { MyPlan };
export default withTheme(MyPlan);
